import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { useIntl } from 'react-intl'
import productLabels from 'i18n/productLabels'
import {
  isExperienceOfferMarkerType,
  isExplorePlaceMarkerType,
  MARKER_TYPE_ITEM,
  isOfferMarkerType,
} from 'tripPlanner/utils/mapMarkerTransforms'
import type { MapMarkerComponent, MarkerType } from 'tripPlanner/utils/mapMarkerTransforms'
import type { LinkRect } from './PlaceHoverPreview/types'
import { HOVER_CLOSE_DELAY_MS } from './PlaceHoverPreview/constants'

export interface HoveredMarkerState {
  placeId?: string
  markerId?: string
  placeName: string
  index: number
  rect: LinkRect
  markerType?: MarkerType
  offerId?: string
  title?: string
  description?: string
  imageId?: string
  imageIds?: Array<string>
}

/** Resolves the marker a pin element renders, via its `data-index` into `markers`. */
export function getMarkerForElement(element: HTMLElement, markers: ReadonlyArray<MapMarkerComponent>): { index: number, marker: MapMarkerComponent } | undefined {
  const { index: markerIndex } = element.dataset
  if (!markerIndex) return undefined
  const index = parseInt(markerIndex, 10)
  const marker = markers[index]
  return marker ? { index, marker } : undefined
}

interface UseMarkerHoverOptions {
  markers: ReadonlyArray<MapMarkerComponent>
  mapContainerRef: RefObject<HTMLElement | null>
  onMarkerHover?: (markerId: string | undefined) => void
  enabled?: boolean
}

/**
 * Encapsulates marker hover state and the delayed-close lifecycle used by
 * the map popover previews.
 *
 * A centralized `setHoverState` keeps the local `hoveredMarker` (controls
 * which popover is shown) and the parent `onMarkerHover` callback (controls
 * the drawer card highlight) in sync.
 *
 * `clearHover` is exposed for external handlers (map move, marker click)
 * that need to dismiss the popover without going through the delayed-close
 * path.
 */
export function useMarkerHover({ markers, mapContainerRef, onMarkerHover, enabled = true }: UseMarkerHoverOptions) {
  const intl = useIntl()
  const fallbackLabels = useMemo(() => ({
    experience: intl.formatMessage({
      id: 'aAZflHUZqYEt1NHXVllx',
      defaultMessage: 'Experience',
      description: 'Fallback place name shown in the map hover popover when an experience marker has no name',
    }),
    hotel: intl.formatMessage(productLabels.hotel),
    place: intl.formatMessage({
      id: 'yyTyEDfPOdjdmg1ZJ5Us',
      defaultMessage: 'Place',
      description: 'Fallback place name shown in the map hover popover when a generic place marker has no name',
    }),
  }), [intl])
  const [hoveredMarker, setHoveredMarker] = useState<HoveredMarkerState | null>(null)
  const hoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const setHoverState = useCallback((marker: HoveredMarkerState | null) => {
    setHoveredMarker(marker)
    // Two lists key off this one id. A marker with no card to highlight clears instead of
    // setting an id that matches nothing.
    const id = isOfferMarkerType(marker?.markerType) ? marker?.offerId :
      isExplorePlaceMarkerType(marker?.markerType) ? marker?.placeId :
        undefined
    onMarkerHover?.(id)
  }, [onMarkerHover])

  const clearHoverTimeout = useCallback(() => {
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current)
      hoverCloseTimeoutRef.current = null
    }
  }, [])

  useEffect(() => clearHoverTimeout, [clearHoverTimeout])

  /** Immediately clear hover state and cancel any pending close timeout. */
  const clearHover = useCallback(() => {
    clearHoverTimeout()
    setHoverState(null)
  }, [clearHoverTimeout, setHoverState])

  useEffect(() => {
    if (!enabled || !hoveredMarker) return
    // Close only for scrolls that move the map; the popover's own image carousel scrolls too.
    const handleScroll = (event: Event) => {
      const mapContainer = mapContainerRef.current
      if (mapContainer && event.target instanceof Node && !event.target.contains(mapContainer)) return
      clearHover()
    }
    document.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', clearHover)
    return () => {
      document.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', clearHover)
    }
  }, [enabled, hoveredMarker, clearHover, mapContainerRef])

  const getHoveredMarkerFromElement = useCallback((element: HTMLDivElement): HoveredMarkerState | null => {
    const resolved = getMarkerForElement(element, markers)
    if (!resolved) return null
    const { index, marker } = resolved

    const domRect = element.getBoundingClientRect()
    const rect: LinkRect = {
      top: domRect.top,
      bottom: domRect.bottom,
      left: domRect.left,
      right: domRect.right,
      width: domRect.width,
      height: domRect.height,
    }
    const { placeId, label, markerId, markerType, title, description, imageId, imageIds } = marker
    const offerId = marker.offerId ?? markerId

    if (isOfferMarkerType(markerType) && offerId) {
      return {
        markerId,
        placeName: label ?? (isExperienceOfferMarkerType(markerType) ? fallbackLabels.experience : fallbackLabels.hotel),
        index,
        markerType,
        offerId,
        title,
        description,
        imageId,
        imageIds,
        rect,
      }
    }

    if (markerType === MARKER_TYPE_ITEM) {
      return {
        markerId,
        placeId,
        placeName: label ?? fallbackLabels.place,
        index,
        markerType,
        offerId,
        title,
        description,
        imageId,
        imageIds,
        rect,
      }
    }

    if (!placeId) {
      return null
    }

    return {
      placeId,
      markerId,
      placeName: label ?? fallbackLabels.place,
      index,
      markerType,
      title,
      description,
      imageId,
      imageIds,
      rect,
    }
  }, [fallbackLabels, markers])

  const setHoveredMarkerFromElement = useCallback((element: HTMLDivElement) => {
    clearHoverTimeout()
    setHoverState(getHoveredMarkerFromElement(element))
  }, [clearHoverTimeout, getHoveredMarkerFromElement, setHoverState])

  const handleMarkerMouseEnter = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!enabled) return
    setHoveredMarkerFromElement(event.currentTarget)
  }, [enabled, setHoveredMarkerFromElement])

  const handleMarkerMouseLeave = useCallback(() => {
    if (!enabled) return
    clearHoverTimeout()
    hoverCloseTimeoutRef.current = setTimeout(() => {
      setHoverState(null)
      hoverCloseTimeoutRef.current = null
    }, HOVER_CLOSE_DELAY_MS)
  }, [clearHoverTimeout, enabled, setHoverState])

  const handlePopoverMouseEnter = useCallback(() => {
    clearHoverTimeout()
  }, [clearHoverTimeout])

  const handlePopoverClose = useCallback(() => {
    setHoverState(null)
  }, [setHoverState])

  return {
    hoveredMarker,
    clearHover,
    handleMarkerMouseEnter,
    handleMarkerMouseLeave,
    handlePopoverMouseEnter,
    handlePopoverClose,
  } as const
}
